import { UploadIcon } from '@/components/bs-icons/upload'
import { aiIconGenerateApi, getSignUrl, uploadFile } from '@/controllers/API/common'
import axios from '@/controllers/request'
import { useDictStore } from '@/store/dictStore'
import { blobToArrayBuffer, convertBase64ToBlob } from '@/util/utils'
import { generateUUID } from '@/utils'
import { LoadingOutlined } from '@ant-design/icons'
import type { UploadProps } from 'antd'
import { Divider, Segmented, Upload } from 'antd'
import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import { showMessage } from '../message'
import './index.css'

/**
 * 图标上传组件
 *
 * 这是一个功能丰富的图标上传组件，支持三种模式：文件上传、通用图标选择、AI生成图标。
 * 集成了文件上传、图片预览、AI图标生成等功能，适用于各种需要图标选择的场景。
 *
 * @param defaultFileList - 默认文件列表
 * @param logo - 当前选中的图标URL
 * @param onSelect - 图标选择回调函数，参数为选中的图标URL
 * @param setDefault - 设置默认值的回调函数
 * @param form - 表单实例，用于表单验证
 * @param validateNames - 需要验证的字段名称数组
 * @param name - 图标名称（用于AI生成）
 * @param description - 图标描述（用于AI生成）
 * @param validateMethod - 自定义验证方法
 * @param props - 其他传递给组件的属性
 *
 * 使用场景：
 * 1. 应用图标设置 - 设置应用的图标
 * 2. 用户头像上传 - 用户头像的选择和上传
 * 3. 品牌标识设置 - 品牌logo的设置
 * 4. 分类图标选择 - 为分类选择图标
 * 5. 功能模块图标 - 为功能模块设置图标
 * 6. 菜单图标配置 - 菜单项的图标配置
 * 7. 卡片图标设置 - 卡片组件的图标设置
 * 8. AI图标生成 - 通过AI生成自定义图标
 *
 * 使用示例：
 * ```tsx
 * // 基础用法
 * <IconUpload
 *   logo={currentIcon}
 *   onSelect={(iconUrl) => setCurrentIcon(iconUrl)}
 * />
 *
 * // 带表单验证
 * <IconUpload
 *   form={form}
 *   validateNames={['name', 'description']}
 *   onSelect={handleIconSelect}
 * />
 *
 * // 自定义验证
 * <IconUpload
 *   name={iconName}
 *   description={iconDescription}
 *   validateMethod={() => validateIconInfo()}
 *   onSelect={handleIconSelect}
 * />
 *
 * // 带默认文件列表
 * <IconUpload
 *   defaultFileList={existingFiles}
 *   logo={currentLogo}
 *   onSelect={setCurrentLogo}
 * />
 * ```
 */

// 组件支持的三种模式类型
type Align = '上传' | '通用' | 'AI生成'

/**
 * 检查文件是否为图片格式
 * @param file - 要检查的文件对象
 * @returns 是否为支持的图片格式（png、jpg、jpeg）
 */
function isImageFile(file) {
  const fileName = file.name
  const extension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase()
  return extension === 'png' || extension === 'jpg' || extension === 'jpeg'
}

const App = ({
  defaultFileList = [],
  logo = '',
  onSelect,
  setDefault = val => {},
  form = null, // 外层使用form校验时直接传入form
  validateNames = [], // 按名称和描述顺序传入
  name = '',
  description = '',
  validateMethod = undefined, // 外层非form校验时传入名称和描述，并传入外层定义的校验函数
  ...props
}) => {
  const [alignValue, setAlignValue] = React.useState<Align>('上传')
  const [logoUrl, setLogoUrl] = React.useState(logo)
  const [ailogoUrl, setAilogoUrl] = React.useState('')
  const { loadDictOptions } = useDictStore()

  const { Dragger } = Upload

  const [typeList, setTypeList] = React.useState<any[]>([])
  // ai loading
  const [AICreateLoading, setAICreateLoading] = useState(false)

  /**
   * 初始化通用图标数据
   * 从字典中加载图标选项列表
   * @param type - 字典类型，默认为'icon'
   */
  const initData = (type = 'icon') => {
    loadDictOptions(type).then(res => {
      setTypeList(res)
      if (res && res.length) {
        setDefault(res[0].value) // 设置第一个选项为默认值
      }
    })
  }

  /**
   * 生成AI图标
   * 根据不同的验证方式（表单验证或自定义验证）来生成AI图标
   */
  const generateAiIcon = () => {
    if (form) {
      // 使用表单验证
      form
        .validateFields(validateNames)
        .then(values => {
          setAICreateLoading(true)
          console.log('form validateFields values', values)
          getAiIconBase64Info(values[validateNames[0]], values[validateNames[1]])
        })
        .catch(err => {
          console.log('err', err)
          setAICreateLoading(false)
        })
    } else if (validateMethod) {
      // 使用自定义验证方法
      const status = validateMethod()
      console.log('validateMethod status', status)
      if (status) {
        setAICreateLoading(true)
        getAiIconBase64Info(name, description)
      } else {
        setAICreateLoading(false)
      }
    } else {
      // 无验证方式，直接设置为不加载状态
      setAICreateLoading(false)
    }
  }

  /**
   * 获取AI生成的图标Base64信息
   * @param name - 图标名称
   * @param description - 图标描述
   */
  const getAiIconBase64Info = (name, description) => {
    const iconInfo = {
      name,
      description,
      width: 512, // 图标宽度
      height: 512 // 图标高度
    }
    aiIconGenerateApi(iconInfo)
      .then(res => {
        uploadBase64Img(res.image) // 上传生成的图标
      })
      .catch(err => {
        console.log(err)
        setAICreateLoading(false) // 生成失败，停止加载状态
      })
  }

  /**
   * 上传Base64图片
   * 将AI生成的Base64图片上传到服务器
   * @param base64Str - Base64格式的图片字符串
   */
  const uploadBase64Img = async (base64Str: string) => {
    try {
      const fileName = generateUUID(32) + '.png' // 生成唯一文件名
      let { url, preview_url } = await getSignUrl(fileName) // 获取上传签名URL

      // 如果是HTTPS环境，将HTTP URL替换为HTTPS
      window.location.origin.startsWith('https') && (url = url.replace('http://', 'https://'))

      const fileBlob = convertBase64ToBlob(base64Str) // 将Base64转换为Blob
      blobToArrayBuffer(fileBlob, arrayBuffer => {
        uploadFile(url, arrayBuffer) // 上传文件
          .then(res => {})
          .finally(() => {
            setAICreateLoading(false) // 停止加载状态
            setAilogoUrl(preview_url) // 设置AI生成的图标URL
            // setLogoUrl(preview_url) // 可选：直接设置为当前选中图标
          })
      })
    } catch (error) {
      console.log(error)
      setAICreateLoading(false) // 上传失败，停止加载状态
    }
  }

  useEffect(() => {
    initData()
    if (alignValue == 'AI生成' && !ailogoUrl) {
      generateAiIcon()
    }
  }, [alignValue])

  useEffect(() => {
    setLogoUrl(logo)
  }, [logo])

  useEffect(() => {
    onSelect(logoUrl)
  }, [logoUrl])

  /**
   * 上传图片文件
   * 处理文件上传逻辑，支持图片裁剪
   * @param options - 上传选项，包含onSuccess、onError、file、onProgress等回调
   */
  const uploadImage = async options => {
    const { onSuccess, onError, file, onProgress } = options

    try {
      const formData = new FormData()
      formData.append('file', file)
      const response: any = await axios.post('/api/v1/common/upload/icon', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      console.log('文件上传成功:', response)
      // setLogoUrl(response?.file_path)
      // onSuccess({ file, preview_url: response?.file_path })
      // 优先使用裁剪后的图片路径，如果没有则使用原图片路径
      /* crop_file_path = 裁剪后的图片 */
      setLogoUrl(response?.crop_file_path || response?.file_path)
      onSuccess({ file, preview_url: response?.crop_file_path || response?.file_path })
    } catch (err) {
      console.error('Error uploading file:', err)
      onError({ err })
    }
  }

  // 文件列表状态
  const [kFileList, setKFileList] = useState([])

  // 上传组件配置
  const UploadProps: UploadProps = {
    name: 'file', // 上传文件的字段名
    multiple: false, // 不支持多文件上传
    maxCount: 1, // 最大文件数量为1
    defaultFileList, // 默认文件列表
    accept: '.png, .jpg, .jpeg', // 接受的文件类型
    customRequest: uploadImage, // 自定义上传请求

    // 文件上传前校验（已注释的旧版本）
    // beforeUpload: file => {
    //   // 文件上传前校验
    //   if (file.size > 10240000) {
    //     message.error(`文件过大，请重新选择10M以内的文件`)
    //     return Upload.LIST_IGNORE
    //   } else {
    //     return true
    //   }
    // },

    /**
     * 文件状态变化处理
     * @param info - 文件信息对象
     */
    onChange(info) {
      const { status } = info.file
      if (status !== 'uploading') {
        // console.log(info.file, info.fileList)
      }
      if (status === 'done') {
        // onSelect(info.file.response.data.file_path)
        // message.success(`${info.file.name} file uploaded successfully.`)
      } else if (status === 'error') {
        // message.error(`${info.file.name} file upload failed.`)
      }

      // 过滤并处理文件列表
      const realFiles = info.fileList
        .filter(file => file.status)
        .map(item => {
          if (item.error) {
            item.error.message = 'Upload Failed'
          }
          return item
        })
      setKFileList(realFiles)
    },

    /**
     * 文件上传前校验
     * @param file - 要上传的文件
     * @returns 是否允许上传
     */
    beforeUpload: file => {
      // 检查文件大小（限制500KB）
      const isLt50KB = file.size / 1024 < 500

      if (!isLt50KB) {
        showMessage({
          content: `图片: ${file.name} 超过500k，请重新上传`,
          type: 'error'
        })
      }

      // 检查文件格式
      const isImg = isImageFile(file)
      if (!isImg) {
        showMessage({
          content: `${file.name} 格式错误，请上传.png, .jpg, .jpeg格式的图片`,
          type: 'error'
        })
      }

      return isLt50KB && isImg // 只有大小和格式都符合要求才允许上传
    },

    /**
     * 拖拽文件处理
     * @param e - 拖拽事件
     */
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files)
    },

    /**
     * 移除文件处理
     * @param file - 要移除的文件
     */
    onRemove: file => {
      defaultFileList = []
      setLogoUrl('') // 清空当前选中的图标
    }
  }

  return (
    <>
      <span className="plugin-family "> icon</span>
      <Divider className="ml-[14px] mr-[14px]" type="vertical" />
      <Segmented
        defaultValue="上传"
        style={{ marginBottom: 8 }}
        onChange={value => setAlignValue(value as Align)}
        options={['上传', '通用', 'AI生成']}
        className="Segmented_font"
      />
      {alignValue == '上传' && (
        <div className="common-icon-upload-container" style={{ marginTop: '16px' }}>
          <Dragger {...UploadProps} fileList={kFileList}>
            <p className="ant-upload-drag-icon mb-[13px] text-center">
              <UploadIcon className="inline-block group-hover:text-primary" />
            </p>
            <p className="common-upload-sub-text text-[14px]">{t('code.clickOrDragHere')}</p>
          </Dragger>
        </div>
      )}
      {alignValue == '通用' && (
        <div style={{ marginTop: '16px' }}>
          {typeList.map((item, index) => (
            <img
              key={index}
              className="mb-[10px] mr-[10px] inline-block cursor-pointer hover:bg-nav-hover"
              width={32}
              height={32}
              src={item.value}
              onClick={() => setLogoUrl(item.value)}
            />
          ))}
        </div>
      )}
      <div className="flexRowStart mt-[10px]">
        <span className="flexRowStart">
          <span className="iconUploadTitle">当前选中：</span>
          {/* {logoUrl && <Image className="select inline-block" src={logoUrl} />} */}
          {logoUrl && <img className="iconUploadImg" src={logoUrl} />}
        </span>
        {/* 展示生成ai图 */}
        {alignValue == 'AI生成' && (
          <span className="flexRowStart" style={{ margin: '0 16px' }}>
            <span className="iconUploadTitle">生成结果：</span>
            {ailogoUrl && (
              <img
                className="cursonCase iconUploadImg"
                onClick={() => {
                  setLogoUrl(ailogoUrl)
                }}
                src={ailogoUrl}
              />
            )}
          </span>
        )}
        {/* ai生成 */}
        {alignValue == 'AI生成' && (
          <span
            onClick={() => {
              generateAiIcon()
            }}
            className={AICreateLoading ? 'aiIconCase aiIconCase_loading' : 'aiIconCase flexRow cursonCase'}
          >
            {AICreateLoading && <LoadingOutlined style={{ marginRight: '5px' }} />}
            {AICreateLoading ? '生成中' : '重新生成'}
          </span>
        )}
      </div>
    </>
  )
}

export default App
